/* eslint-disable */
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { Spin, Tooltip } from 'antd';
import useIsMobileClass from '@/components/isMobileClass';

import { Card } from '../Layout';
import Rate from '../Rate';

// import defaultPlaceholderImage from '@/assets/images/no-bg.svg';
import useImage from '@shared/hooks/useImage';

import Utils from '@/Utils';
import classNames from 'classnames';
import _ from 'lodash';
import { noun } from 'plural-ru';
import PropTypes from 'prop-types';

import css from './CompilationCard.module.scss';

const CompilationCard = ({ compilation, dataqa, searchParams, setSearchParams, shortDescription, isFavorite }) => {
  const [themes, setThemes] = useState(compilation.themes);
  const [isTextOverflown, setIsTextOverflown] = useState(false);
  const themesContainerRef = useRef(null);
  const { t } = useTranslation('compilationCard');
  const navigate = useNavigate();

  const coverImage = useImage(compilation.cover, 'compilation');

  useEffect(() => {
    if (themesContainerRef.current) {
      const isOverflown = themesContainerRef.current.offsetWidth < themesContainerRef.current.scrollWidth;
      if (isOverflown !== isTextOverflown) {
        setIsTextOverflown(isOverflown);
      }
    }
  }, [themes, isTextOverflown]);

  const renderThemes = () => {
    const maxLenThemes = useIsMobileClass() ? 25 : 35;
    const themesArr = themes.map(theme => theme.name);

    const { themesViewLength, totalLength } = themesArr.reduce(
      (acc, curr, index) => {
        const updatedLength = acc.totalLength + curr.length;

        if (updatedLength > maxLenThemes && acc.themesViewLength === themesArr.length) {
          return {
            totalLength: updatedLength,
            themesViewLength: index + 1,
          };
        }

        return {
          totalLength: updatedLength,
          themesViewLength: acc.themesViewLength,
        };
      },
      { totalLength: 0, themesViewLength: themesArr.length }
    );

    const themesView = themes.slice(0, themesViewLength);
    const isShotThemesLengthVal = totalLength > maxLenThemes;
    const difference = themes.length - themesViewLength;

    const list = isShotThemesLengthVal
      ? [
          ...themesView.slice(0, themesView.length - 1),
          themesView[themesView.length - 1] && {
            id: themesView[themesView.length - 1].id,
            name: themesView[themesView.length - 1].name,
          },
        ]
      : themesView;

    return (
      <div className={classNames(css.CompilationCard__infoCard__card__themesHeader)}>
        <div
          className={classNames(css.CompilationCard__infoCard__card__themesHeader__themesPreview)}
          ref={themesContainerRef}
        >
          {list.map((theme, key) => (
            <span key={`${key}-${theme.id}`}>
              {isTextOverflown && key === themesViewLength - 1 ? (
                <Tooltip title={theme.name} mouseEnterDelay={0.3}>
                  <a onClick={() => setSearchParams(theme.id)}>{theme.name}</a>
                </Tooltip>
              ) : (
                <a onClick={() => setSearchParams(theme.id)}>{theme.name}</a>
              )}
              {list?.length > key + 1 && ', '}
            </span>
          ))}
        </div>
        {isShotThemesLengthVal && difference ? (
          <div className={css.CompilationCard__seeMore}>
            <span>{t('more', { difference })}</span>
            <Card className={css.CompilationCard__seeMore__card}>
              {themes.slice(themes.length - difference, themes.length).map((theme, key) => (
                <span key={`${key}-${theme.id}`}>
                  <a
                    onClick={() =>
                      setSearchParams({
                        ...searchParams,
                        themeId: [theme.id],
                      })
                    }
                  >
                    {theme.name}
                  </a>
                  {difference > key + 1 && ', '}
                </span>
              ))}
            </Card>
          </div>
        ) : null}
      </div>
    );
  };

  const handleReferrerLink = () => localStorage.setItem('referrer', document.location.pathname);

  const handleLink = () => {
    navigate(`/compilations/${compilation.id}`);
  };

  const mainNameMaxLen = useIsMobileClass() ? 20 : 90;
  const mainDiscMaxLen = useIsMobileClass() ? 30 : 90;

  return (
    <Card
      data-qa='compilationCard'
      className={classNames(css.CompilationCard, {
        [css.Favorite]: isFavorite,
      })}
      onMouseDown={handleReferrerLink}
    >
      <div className={css.CompilationCard__content}>
        <div className={css.CompilationCard__cover} onClick={handleLink}>
          <img src={coverImage} alt='' />
        </div>
        <div className={css.CompilationCard__info}>
          <div>
            <div data-qa={`${dataqa}-themes`} className={css.CompilationCard__themes}>
              {renderThemes()}
            </div>
            <Link
              data-qa={`${dataqa}-name`}
              className={css.CompilationCard__name}
              to={`/compilations/${compilation.id}`}
            >
              {Utils.renderWithTooltip(compilation.name, mainNameMaxLen)}
            </Link>
            <div data-qa={`${dataqa}-shortDescription`} className={css.CompilationCard__sub}>
              {Utils.renderWithTooltip(shortDescription, mainDiscMaxLen)}
            </div>
          </div>
          <Card className={css.CompilationCard__infoCard} style={{ borderRadius: 8 }}>
            <div
              className={classNames(css.CompilationCard__infoCard__card, {
                [css.Favorite__cardInfo]: isFavorite,
              })}
            >
              <div
                className={classNames(
                  css.CompilationCard__infoCard__card__col,
                  css.CompilationCard__infoCard__card__col__sizeCount
                )}
              >
                <div className={css.CompilationCard__infoCard__card__col__count}>{t('count')}</div>
                <p className={css.CompilationCard__infoCard__card__col__count__value}>
                  {`${compilation.count} `}
                  {noun(compilation.count, t('material'), t('material2'), t('material3'))}
                </p>
              </div>
              <div
                className={classNames(
                  css.CompilationCard__infoCard__card__col,
                  css.CompilationCard__infoCard__card__col__sizeRate
                )}
              >
                <div className={css.CompilationCard__infoCard__card__col__rate}>{t('rate')}</div>
                <Rate noChange count={5} value={compilation.rate} disabled />
              </div>
              {compilation.manager && (
                <div
                  className={classNames(
                    css.CompilationCard__infoCard__card__col,
                    css.CompilationCard__infoCard__card__col__sizeExpert
                  )}
                >
                  <div className={css.CompilationCard__infoCard__card__col__expert}>{t('expert')}</div>
                  <div
                    data-qa={`${dataqa}-expert`}
                    className={css.CompilationCard__autor}
                    onClick={() => navigate(`/collegues/${compilation.manager.id}`)}
                  >
                    {compilation.manager.name.length > 24
                      ? compilation.manager.name.split(0, 21) + '...'
                      : compilation.manager.name}
                  </div>
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>
    </Card>
  );
};

CompilationCard.propTypes = {
  compilation: PropTypes.object,
  dataqa: PropTypes.string,
  searchParams: PropTypes.object,
  setSearchParams: PropTypes.func,
  shortDescription: PropTypes.string,
  isFavorite: PropTypes.bool,
};

export default CompilationCard;
